import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Skeleton } from '@mui/material'
import * as R from 'ramda'
import {
  type Field,
  moment,
  type NamedEntity,
  Utils,
} from '@pbt/pbt-ui-components'

import EnumSelect, {
  type EnumSelectProps,
} from '~/components/common/inputs/EnumSelect'
import { TaskState, UncreatableTasks } from '~/constants/taskConstants'
import { useTaskMarkAsDoneDisabled } from '~/hooks/useTaskMarkAsDoneDisabled'
import { queueEasterEggEvent } from '~/store/actions/easterEgg'
import { editTaskState } from '~/store/actions/tasks'
import { useTaskEvent } from '~/store/hooks/tasks'
import { getTasksIsLoading } from '~/store/reducers/tasks'
import { EasterEggEvents, type Task } from '~/types'

export interface TaskStatusSelectProps extends Partial<EnumSelectProps> {
  disabled?: boolean
  fullWidth?: boolean
  task?: Task
}

const TaskStatusSelect = ({
  task,
  disabled: disabledProp,
  fullWidth,
  ...rest
}: TaskStatusSelectProps) => {
  const dispatch = useDispatch()
  const isLoading = useSelector(getTasksIsLoading)
  const { states: TaskStates, subTypes: TaskSubTypes } = useTaskEvent()
  const isMarkAsDoneDisabled = useTaskMarkAsDoneDisabled(task)

  if (!task || R.isEmpty(task)) {
    return <Skeleton height={20} variant="text" width={70} />
  }

  const OpenStateId = Utils.findConstantIdByName(TaskState.OPEN, TaskStates)
  const OverdueStateId = Utils.findConstantIdByName(
    TaskState.OVERDUE,
    TaskStates,
  )
  const DoneStateId = Utils.findConstantIdByName(TaskState.DONE, TaskStates)

  const expired =
    task.stateId === OverdueStateId && moment().isAfter(task.dueDate)

  const filterFunc = (state: NamedEntity) =>
    !expired || state.id !== OpenStateId

  const FilteredTaskStates = TaskStates.filter(filterFunc)

  const setState = (stateId: string) => {
    if (stateId === DoneStateId) {
      dispatch(queueEasterEggEvent({ actionType: EasterEggEvents.TASK_DONE }))
    }
    dispatch(editTaskState(task.id, stateId))
  }

  const disabled =
    disabledProp ||
    UncreatableTasks.some(
      (type) =>
        task.typeId === Utils.findConstantByName(type, TaskSubTypes)?.id,
    ) ||
    isMarkAsDoneDisabled

  return (
    <EnumSelect
      accent
      Constant={FilteredTaskStates}
      disabled={disabled || isLoading}
      field={
        {
          value: task.stateId,
          set: Utils.handleFormSelectInput(setState),
        } as Field
      }
      fullWidth={fullWidth}
      {...rest}
    />
  )
}

export default TaskStatusSelect
